import type { ProviderConnectedEvent } from '@/features/integrations'

export type ConnectionFollowup =
  | { kind: 'branded-keywords' }
  | { kind: 'source-medium'; platform: 'google_ads' | 'meta_ads' | 'tiktok_ads' }
  | { kind: 'product-id-mapping'; platform: 'merchant_center' | 'meta_catalog' }
  | { kind: 'key-events'; platform: 'ga4' | 'adjust' | 'appsflyer'; accountId: string | null }
  | { kind: 'adjust-configuration' }
  | { kind: 'hardal-key-events' }

export interface ConnectedResultFacts {
  accountIds: readonly string[]
  adAccountsRegistered: boolean | null
  catalogPreviewRequiresMapping: boolean
}

const ACCOUNT_ID_KEYS = [
  'account_id',
  'accountId',
  'property_id',
  'propertyId',
  'client_customer_id',
] as const

const CATALOG_FLAG_KEYS = [
  'catalog_connected',
  'catalogConnected',
  'catalog_preview_required',
  'catalogPreviewRequired',
  'has_catalog',
  'hasCatalog',
] as const

const CATALOG_VALUE_KEYS = ['catalog_id', 'catalogId', 'catalog'] as const
const SOURCE_MEDIUM = 'source-medium' as const
const PRODUCT_ID_MAPPING = 'product-id-mapping' as const
const KEY_EVENTS = 'key-events' as const

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function asIdentifier(value: unknown): string | null {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : null
}

/** Project the family-specific register response into the only facts the Settings host needs. */
export function projectConnectedResult(result: unknown): ConnectedResultFacts {
  const accountIds = new Set<string>()
  let adAccountsRegistered: boolean | null = null
  let catalogPreviewRequiresMapping = false

  function visit(value: unknown, depth: number): void {
    if (depth > 4) return
    if (Array.isArray(value)) {
      for (const item of value) visit(item, depth + 1)
      return
    }
    if (!isRecord(value)) return

    if (typeof value.adAccountsRegistered === 'boolean') {
      adAccountsRegistered = value.adAccountsRegistered
    } else if (typeof value.ad_accounts_registered === 'boolean') {
      adAccountsRegistered = value.ad_accounts_registered
    }

    for (const key of ACCOUNT_ID_KEYS) {
      const id = asIdentifier(value[key])
      if (id) accountIds.add(id)
    }
    for (const key of CATALOG_FLAG_KEYS) {
      if (value[key] === true) catalogPreviewRequiresMapping = true
    }
    for (const key of CATALOG_VALUE_KEYS) {
      const catalog = value[key]
      if (catalog !== undefined && catalog !== null && catalog !== false && catalog !== '') {
        catalogPreviewRequiresMapping = true
      }
    }
    for (const nested of Object.values(value)) visit(nested, depth + 1)
  }

  visit(result, 0)
  return { accountIds: [...accountIds], adAccountsRegistered, catalogPreviewRequiresMapping }
}

/**
 * The one exhaustive legacy post-connect matrix. Provider registry keys and projected response
 * facts are stable domain identifiers; translated display labels never participate.
 */
export function resolveConnectionFollowups(event: ProviderConnectedEvent): ConnectionFollowup[] {
  const facts = projectConnectedResult(event.result)
  const accountId = facts.accountIds[0] ?? null

  switch (event.provider.key) {
    case 'google_ads_account':
      return [{ kind: 'branded-keywords' }, { kind: SOURCE_MEDIUM, platform: 'google_ads' }]
    case 'ga4_analytics_property':
      return [{ kind: KEY_EVENTS, platform: 'ga4', accountId }]
    case 'merchant_center_account':
      return [{ kind: PRODUCT_ID_MAPPING, platform: 'merchant_center' }]
    case 'meta_ads_account':
      return [
        ...(facts.adAccountsRegistered === false
          ? []
          : ([{ kind: SOURCE_MEDIUM, platform: 'meta_ads' }] as const)),
        ...(facts.catalogPreviewRequiresMapping
          ? ([{ kind: PRODUCT_ID_MAPPING, platform: 'meta_catalog' }] as const)
          : []),
      ]
    case 'tiktok_ads_account':
      // A TikTok connect can register only pixels or only catalogs, and v3 opened Source/Medium
      // solely when an ad account was among them (`TiktokIntegration.vue:366`). The branch became
      // reachable when the TikTok connector landed (ORP-18700).
      return facts.adAccountsRegistered === false
        ? []
        : [{ kind: SOURCE_MEDIUM, platform: 'tiktok_ads' }]
    case 'adjust_account':
      return [{ kind: KEY_EVENTS, platform: 'adjust', accountId }, { kind: 'adjust-configuration' }]
    case 'appsflyer_account':
      return [{ kind: KEY_EVENTS, platform: 'appsflyer', accountId }]
    case 'hardal_account':
      return [{ kind: 'hardal-key-events' }]
    default:
      return []
  }
}
